"use client"

import { useState, useCallback, useEffect, useRef } from "react"
import dynamic from "next/dynamic"
import { useRouter } from "next/navigation"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"
import { motion } from "motion/react"
import { FADE_UP, FADE_TRANSITION } from "@/lib/motion-presets"
import { Reveal } from "@/components/motion"
import { Loader2, Thermometer, MapPin, Calendar, TrendingUp, TrendingDown, Minus } from "lucide-react"
import { HeadlineHero, LawnPrefillBanner, RelatedTools, ResultEmailCaptureCard } from "@/components/tools/shared"
import { ProductGuideCta } from "@/components/lead-magnets/product-guide-cta"
import { UnifiedShareButton } from "@/components/tools/shared/unified-share-button"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { ZipCodeInput, type ZipValidationResult } from "@/components/ui/zip-code-input"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { SoilTempChartSkeleton } from "./components/soil-temp-chart-skeleton"
import type { SoilTempChartHandle } from "./components/soil-temp-chart"

// Dynamically load the recharts-using chart components so the ~250KB recharts
// library lands in a deferred chunk instead of the initial bundle for
// /tools/soil-temperature. ssr:false because recharts depends on browser-only
// DOM measurement APIs.
const SoilTempChart = dynamic(
  () => import("./components/soil-temp-chart").then((m) => ({ default: m.SoilTempChart })),
  { ssr: false, loading: () => <SoilTempChartSkeleton /> }
)
import { LayerToggle } from "./components/layer-toggle"
import { DateRangePicker } from "./components/date-range-picker"
import { STATUS_STYLES, getToolForPhase } from "./components/recommendation-card"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import Link from "next/link"
import { trackEvent } from "@/lib/analytics"
import {
  getSoilTempRecommendation,
  getTempDifference,
  mergeForChart,
  aggregateChartToMonthly,
  type GrassCategory,
} from "@/lib/soil-temp-transforms"
import type { SoilTemperatureResponse } from "@/lib/soil-temperature/types"
import type { HistoricalAvgResponse } from "@/lib/year-calendar-types"
import { analytics, stashEmailCaptureAttribution } from "@/lib/analytics"
import { buildToolUrl } from "@/lib/result-tool-urls"
import { useFromTool } from "@/hooks/use-from-tool"
import { useAuth } from "@/contexts/AuthContext"
import { useNewsletterPrompt } from "@/hooks/useNewsletterPrompt"
import { NewsletterSignupModal } from "@/components/ui/newsletter-signup-modal"
import { ProductRecommendations } from "@/components/tools/product-recommendations"
import { RegionalSoilTempTable } from "./components/regional-soil-temp-table"
import { CollapsibleSection } from "@/components/tools/shared/collapsible-section"
import { YearCalendar } from "@/components/tools/shared/year-calendar"
import { buildSoilTempCalendar } from "@/lib/soil-temp-month-grid"
import { useDefaultLawn } from "@/hooks/use-default-lawn"

// Lazy-load the leaflet-based national map. leaflet evaluates window
// at import time, so ssr:false is required. This also keeps leaflet
// (~45KB gzipped) out of the initial bundle.
const NationalSoilMap = dynamic(
  () =>
    import("./components/national-soil-map").then((m) => ({
      default: m.NationalSoilMap,
    })),
  {
    ssr: false,
    loading: () => (
      <div className="h-[420px] w-full animate-pulse rounded-lg border border-gray-200 bg-gray-100" />
    ),
  }
)

import { getProductHintsForPhase } from "@/data/soil-temp-products"

type Layer = "current" | "5yr" | "10yr"

interface DateRange {
  start: Date
  end: Date
}

// Tailwind v4 default sm breakpoint. Mobile-only defaults are applied after
// mount so SSR and hydration start from the same markup.
const MOBILE_BREAKPOINT_PX = 640

function isMobileViewport(): boolean {
  return typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT_PX
}

// Pull the Amazon ASIN out of an affiliate URL so the secondary thermometer
// product block can suppress a thermometer the phase block above already showed.
const ASIN_URL_PATTERNS = [
  /\/dp\/([A-Z0-9]{10})(?:[/?]|$)/i,
  /\/gp\/product\/([A-Z0-9]{10})(?:[/?]|$)/i,
  /[?&]asin=([A-Z0-9]{10})(?:&|$)/i,
]
function extractAsinFromUrl(url?: string | null): string | undefined {
  if (!url) return undefined
  for (const pattern of ASIN_URL_PATTERNS) {
    const match = url.match(pattern)
    if (match) return match[1].toUpperCase()
  }
  return undefined
}

function getLocationInputType(location: string): "zip" | "city" | "empty" {
  const trimmed = location.trim()
  if (!trimmed) return "empty"
  return /^\d{5}$/.test(trimmed) ? "zip" : "city"
}

function getZipPrefix(location: string): string | undefined {
  const trimmed = location.trim()
  return /^\d{5}$/.test(trimmed) ? trimmed.slice(0, 3) : undefined
}

function getStateFromLabel(label?: string): string | undefined {
  const match = label?.match(/,\s*([A-Z]{2})(?:\s|$)/)
  return match?.[1]
}

function getCoordinateBand(value?: number): string | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined
  const lower = Math.floor(value / 5) * 5
  return `${lower} to ${lower + 5}`
}

function getDateRangeDays(range: DateRange): number {
  return Math.ceil(
    (range.end.getTime() - range.start.getTime()) / (1000 * 60 * 60 * 24)
  )
}

/** Carries the API's stable `code` alongside the human-readable message. */
class SoilTempApiError extends Error {
  code?: string
  constructor(message: string, code?: string) {
    super(message)
    this.name = "SoilTempApiError"
    this.code = code
  }
}

function getErrorCategory(error: unknown): string {
  // Prefer the API's stable code. Classifying by regex over the user-facing
  // prose collapsed six distinct server errors into "unknown", which is what
  // the failure telemetry recorded for almost every failed search. The keyword
  // pass below remains for transport failures, which never reach the route.
  if (error instanceof SoilTempApiError && error.code) return error.code

  const message = error instanceof Error ? error.message : String(error)
  const lower = message.toLowerCase()
  if (lower.includes("location") || lower.includes("geocod")) return "location_lookup"
  if (lower.includes("rate") || lower.includes("429")) return "rate_limited"
  if (lower.includes("timeout")) return "timeout"
  if (lower.includes("historical")) return "historical_average"
  if (lower.includes("fetch")) return "weather_fetch"
  return "unknown"
}

// Lightweight relative-age formatter for the hero "Updated N min ago"
// subtitle. Uses Intl.RelativeTimeFormat for proper i18n. Falls back to
// "just now" inside the 30-second sub-resolution band.
function formatRelativeAge(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return "just now"
  const diffMs = Date.now() - then
  const diffSec = Math.round(diffMs / 1000)
  if (diffSec < 30) return "just now"
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" })
  if (diffSec < 60) return rtf.format(-diffSec, "second")
  const diffMin = Math.round(diffSec / 60)
  if (diffMin < 60) return rtf.format(-diffMin, "minute")
  const diffHr = Math.round(diffMin / 60)
  if (diffHr < 24) return rtf.format(-diffHr, "hour")
  const diffDay = Math.round(diffHr / 24)
  return rtf.format(-diffDay, "day")
}

export function SoilTemperaturePageContent() {
  const router = useRouter()
  const searchParams = usePrerenderSafeSearchParams()
  useFromTool("soil-temperature")
  const chartRef = useRef<SoilTempChartHandle | null>(null)
  const { subscription } = useAuth()
  const { lawns, selectedLawnId, setSelectedLawnId, prefill: lawnPrefill } = useDefaultLawn()
  const { trackUsage, showNewsletterModal, closeNewsletterModal, usageCount } = useNewsletterPrompt()
  const urlLocation = searchParams.get("location") || searchParams.get("zip") || ""

  // State
  const [location, setLocation] = useState(urlLocation)
  const [submittedLocation, setSubmittedLocation] = useState<string | null>(null)
  const [zipValidation, setZipValidation] = useState<ZipValidationResult>(
    urlLocation
      ? { status: /^\d{5}$/.test(urlLocation) ? 'resolving' : 'format-invalid', zip: urlLocation }
      : { status: 'empty', zip: '' }
  )
  const [layers, setLayers] = useState<Layer[]>(["current", "5yr"])
  const [grassType, setGrassType] = useState<GrassCategory>("cool")
  // ASINs the phase-matched product block surfaced, so the secondary
  // "Want your own reading?" thermometer block can suppress a duplicate.
  const [phaseProductAsins, setPhaseProductAsins] = useState<string[]>([])
  const userEditedPrefillRef = useRef(false)
  const pendingGeolocationSearchRef = useRef<string | null>(null)
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const end = new Date() // Today
    const start = new Date()
    start.setDate(start.getDate() - 30)
    return { start, end }
  })

  // Data state
  const [currentData, setCurrentData] = useState<SoilTemperatureResponse | null>(null)
  const [fiveYearData, setFiveYearData] = useState<HistoricalAvgResponse | null>(null)
  const [tenYearData, setTenYearData] = useState<HistoricalAvgResponse | null>(null)
  const [loadingStage, setLoadingStage] = useState<
    'idle' | 'geocoding' | 'fetching' | 'historical' | 'complete'
  >('idle')
  const [loadingMessage, setLoadingMessage] = useState<string>("")

  // Labor Perception (growth-design): the live reading comes from a free upstream
  // (Open-Meteo) that occasionally runs slow — the route retries, then falls back
  // to cached data, so the fetch can span ~10s. A frozen "Fetching soil
  // temperatures..." reads as broken in that window, so escalate the message the
  // longer we wait: first name the cause, then (if it really drags) lighten it up.
  // Both timers clear when the stage changes (data arrives / moves to historical).
  useEffect(() => {
    if (loadingStage !== 'fetching') return
    const slow = setTimeout(() => {
      setLoadingMessage("Still working, the weather service is slow...")
    }, 4000)
    const reallySlow = setTimeout(() => {
      setLoadingMessage("Hmm, this usually doesn't take this long...")
    }, 9000)
    return () => {
      clearTimeout(slow)
      clearTimeout(reallySlow)
    }
  }, [loadingStage])

  useEffect(() => {
    if (userEditedPrefillRef.current || urlLocation) return
    if (lawnPrefill.zipCode && !location) setLocation(lawnPrefill.zipCode)
    if (lawnPrefill.grassType) {
      const warm = /bermuda|zoysia|augustine|centipede|bahia|buffalo/i.test(lawnPrefill.grassType)
      setGrassType(warm ? "warm" : "cool")
    }
  }, [lawnPrefill, location, urlLocation])
  const [error, setError] = useState<string | null>(null)
  const viewedResultKeyRef = useRef<string | null>(null)

  // Mobile detection for responsive chart adjustments. Initial state stays
  // server-stable; compact defaults apply after mount.
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const checkMobile = () => {
      const mobile = isMobileViewport()
      setIsMobile(mobile)
      if (mobile && !submittedLocation) {
        setLayers(["current"])
        setDateRange(() => {
          const end = new Date()
          const start = new Date()
          start.setDate(start.getDate() - 14)
          return { start, end }
        })
      }
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [submittedLocation])

  // Fetch current data
  const fetchCurrentData = useCallback(async (loc: string, range: DateRange) => {
    const startStr = range.start.toISOString().split("T")[0]
    const endStr = range.end.toISOString().split("T")[0]

    // Determine if it's a ZIP code or search query
    const isZip = /^\d{5}$/.test(loc.trim())
    const param = isZip ? `zip=${loc.trim()}` : `query=${encodeURIComponent(loc.trim())}`

    const response = await fetch(
      `/api/soil-temperature?${param}&startDate=${startStr}&endDate=${endStr}`
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({} as { error?: string; code?: string }))
      throw new SoilTempApiError(
        errorData.error || "Failed to fetch soil temperature data",
        errorData.code
      )
    }

    return response.json() as Promise<SoilTemperatureResponse>
  }, [])

  // Fetch historical averages
  const fetchHistoricalAvg = useCallback(async (loc: string, years: 5 | 10) => {
    const isZip = /^\d{5}$/.test(loc.trim())
    const param = isZip ? `zip=${loc.trim()}` : `query=${encodeURIComponent(loc.trim())}`

    const response = await fetch(
      `/api/soil-temperature/historical-avg?${param}&years=${years}`
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({} as { error?: string; code?: string }))
      throw new SoilTempApiError(
        errorData.error || `Failed to fetch ${years}-year average`,
        errorData.code
      )
    }

    return response.json() as Promise<HistoricalAvgResponse>
  }, [])

  // Main fetch handler - accepts optional location override for quick-start buttons
  const handleSearch = useCallback(async (
    searchLocation?: string,
    sourceOverride?: 'form_submit' | 'url_params' | 'quick_start'
  ) => {
    const loc = searchLocation || location
    // Every caller passes its own source. The old default inferred 'quick_start'
    // from "was a location argument passed", which the main search button also
    // does, so real form submits were reported as quick starts and
    // 'form_submit' was unreachable.
    const source = sourceOverride ?? 'form_submit'
    const rawInputType = getLocationInputType(loc)
    const startedAt = Date.now()

    if (!loc.trim()) {
      setError("Please enter a location")
      analytics.soilTempSearchFailed({
        inputType: 'empty',
        source,
        stage: 'validation',
        errorCategory: 'empty_location',
      })
      return
    }

    const inputType = rawInputType as 'zip' | 'city'
    let loadedCurrent = false

    analytics.soilTempSearchStarted({
      inputType,
      source,
      dateRangeDays: getDateRangeDays(dateRange),
      activeLayers: layers,
      grassType,
      zipPrefix: getZipPrefix(loc),
    })

    trackUsage('soil-temperature') // Non-blocking, may show newsletter modal after 2nd use

    setLoadingStage('geocoding')
    setLoadingMessage("Finding your location...")
    setError(null)
    setLocation(loc.trim()) // Sync state if passed directly
    setSubmittedLocation(loc.trim())
    // A new query owns the result rail. Clear the prior location immediately
    // so a failed lookup can never leave stale temperatures under a new input.
    setCurrentData(null)
    setFiveYearData(null)
    setTenYearData(null)
    setPhaseProductAsins([])

    try {
      // Fetch current data
      setLoadingStage('fetching')
      setLoadingMessage("Fetching soil temperatures...")
      const current = await fetchCurrentData(loc, dateRange)
      loadedCurrent = true

      // Show current data IMMEDIATELY (chart will render with this)
      setCurrentData(current)

      // Scroll to results area
      setTimeout(() => {
        const el = document.getElementById('soil-temp-results')
        if (el) {
          const y = el.getBoundingClientRect().top + window.scrollY - 100
          const prefersReduced =
            typeof window !== 'undefined' &&
            window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
          window.scrollTo({ top: y, behavior: prefersReduced ? 'auto' : 'smooth' })
        }
      }, 100)

      // Auto-select grass type based on latitude
      // > 37°N → Cool-season (Northern US)
      // < 33°N → Warm-season (Deep South)
      // 33-37°N → Transition zone (default to cool)
      const resolvedGrassType =
        current.location?.lat && current.location.lat < 33 ? "warm" : "cool"

      if (current.location?.lat) {
        const lat = current.location.lat
        if (lat < 33) {
          setGrassType("warm")
        } else {
          setGrassType("cool") // Cool for northern US and transition zone
        }
      }

      analytics.soilTempCurrentLoaded({
        inputType,
        source,
        state: getStateFromLabel(current.location?.label),
        latBand: getCoordinateBand(current.location?.lat),
        lonBand: getCoordinateBand(current.location?.lon),
        grassType: resolvedGrassType,
        latestTempF: current.current?.t6?.[current.current.t6.length - 1] === undefined
          ? undefined
          : Math.round(current.current.t6[current.current.t6.length - 1]),
        trendFPerDay: current.current?.trendFPerDay === undefined
          ? undefined
          : Math.round(current.current.trendFPerDay * 10) / 10,
        dataPoints: current.current?.time?.length,
        dateRangeDays: getDateRangeDays(dateRange),
        activeLayers: layers,
        durationMs: Date.now() - startedAt,
      })

      // Track calculator usage with full inputs + result summary
      analytics.calculatorUsed('soil-temperature', {
        inputs: {
          input_type: inputType,
          zip_prefix: getZipPrefix(loc),
          grass_type: resolvedGrassType,
          date_range_start: dateRange.start.toISOString().slice(0, 10),
          date_range_end: dateRange.end.toISOString().slice(0, 10),
          layers: layers,
          state: getStateFromLabel(current.location?.label),
          lat_band: getCoordinateBand(current.location?.lat),
          lon_band: getCoordinateBand(current.location?.lon),
        },
        result: {
          latest_temp_f: current.current?.t6?.[current.current.t6.length - 1] === undefined
            ? undefined
            : Math.round(current.current.t6[current.current.t6.length - 1]),
          trend_f_per_day: current.current?.trendFPerDay === undefined
            ? undefined
            : Math.round(current.current.trendFPerDay * 10) / 10,
          data_points: current.current?.time?.length,
        },
        userTier: subscription?.isPremium ? 'premium' : 'free',
        location: getStateFromLabel(current.location?.label),
        source,
      })

      // Update URL params
      const params = new URLSearchParams()
      params.set("location", loc.trim())
      router.replace(`?${params.toString()}`, { scroll: false })

      // Fetch historical data in background (chart already visible!).
      // 5-year is fetched unconditionally so the verdict's
      // "X°F cooler than average" line always populates after data loads,
      // regardless of whether the 5-year layer is currently visible on the chart.
      setLoadingStage('historical')
      setLoadingMessage("Adding historical averages...")

      const historicalPromises: Promise<void>[] = [
        (() => {
          const historicalStartedAt = Date.now()
          return fetchHistoricalAvg(loc, 5).then((data) => {
          setFiveYearData(data)
          analytics.soilTempHistoricalLoaded({
            years: 5,
            status: 'success',
            durationMs: Date.now() - historicalStartedAt,
            activeLayers: layers,
          })
          if (layers.includes("10yr")) {
            setLoadingMessage("Loading 10-year average...")
          }
        }).catch((err) => {
          analytics.soilTempHistoricalLoaded({
            years: 5,
            status: 'error',
            durationMs: Date.now() - historicalStartedAt,
            activeLayers: layers,
            errorCategory: getErrorCategory(err),
          })
          throw err
        })
        })(),
      ]

      if (layers.includes("10yr")) {
        historicalPromises.push(
          (() => {
            const historicalStartedAt = Date.now()
            return fetchHistoricalAvg(loc, 10).then((data) => {
              setTenYearData(data)
              analytics.soilTempHistoricalLoaded({
                years: 10,
                status: 'success',
                durationMs: Date.now() - historicalStartedAt,
                activeLayers: layers,
              })
            }).catch((err) => {
              analytics.soilTempHistoricalLoaded({
                years: 10,
                status: 'error',
                durationMs: Date.now() - historicalStartedAt,
                activeLayers: layers,
                errorCategory: getErrorCategory(err),
              })
              throw err
            })
          })()
        )
      }

      await Promise.all(historicalPromises)

      setLoadingStage('idle')
      setLoadingMessage("")
    } catch (err) {
      if (!loadedCurrent) {
        setSubmittedLocation(null)
      }
      setError(err instanceof Error ? err.message : "An error occurred")
      analytics.soilTempSearchFailed({
        inputType,
        source,
        stage: loadedCurrent ? 'historical' : 'current',
        errorCategory: getErrorCategory(err),
        durationMs: Date.now() - startedAt,
      })
      setLoadingStage('idle')
    }
  }, [location, dateRange, layers, fetchCurrentData, fetchHistoricalAvg, router, trackUsage, grassType, subscription])

  // Handle layer changes - fetch missing data and track
  const handleLayerChange = useCallback(
    async (newLayers: Layer[]) => {
      // Track which layer was toggled
      const addedLayer = newLayers.find(l => !layers.includes(l))
      const removedLayer = layers.find(l => !newLayers.includes(l))
      const toggledLayer = addedLayer || removedLayer
      if (toggledLayer) {
        analytics.soilTempLayerToggled(
          toggledLayer as 'current' | '5yr' | '10yr',
          !!addedLayer,
          newLayers
        )
      }

      setLayers(newLayers)

      if (!submittedLocation) return

      // Check if we need to fetch new historical data
      if (newLayers.includes("5yr") && !fiveYearData) {
        try {
          const data = await fetchHistoricalAvg(submittedLocation, 5)
          setFiveYearData(data)
        } catch (err) {
          console.error("Failed to fetch 5-year data:", err)
        }
      }

      if (newLayers.includes("10yr") && !tenYearData) {
        try {
          const data = await fetchHistoricalAvg(submittedLocation, 10)
          setTenYearData(data)
        } catch (err) {
          console.error("Failed to fetch 10-year data:", err)
        }
      }
    },
    [submittedLocation, fiveYearData, tenYearData, fetchHistoricalAvg, layers]
  )

  // Handle single layer toggle (for interactive legend)
  const handleLayerToggle = useCallback(
    (layer: Layer) => {
      const newLayers = layers.includes(layer)
        ? layers.filter((l) => l !== layer)
        : [...layers, layer]

      // Ensure at least one layer is always visible
      if (newLayers.length === 0) return

      handleLayerChange(newLayers)
    },
    [layers, handleLayerChange]
  )

  // Handle date range change - refetch current data
  const handleDateRangeChange = useCallback(
    async (newRange: DateRange) => {
      setDateRange(newRange)
      analytics.soilTempDateRangeChanged({
        dateRangeDays: getDateRangeDays(newRange),
        activeLayers: layers,
        hasResults: !!submittedLocation,
      })

      if (!submittedLocation) return

      setLoadingStage('fetching')
      setLoadingMessage("Updating date range...")
      try {
        const current = await fetchCurrentData(submittedLocation, newRange)
        setCurrentData(current)
        setLoadingStage('idle')
        setLoadingMessage("")
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to update date range")
        setLoadingStage('idle')
      }
    },
    [submittedLocation, fetchCurrentData, layers]
  )

  // Auto-search if location in URL
  useEffect(() => {
    if (urlLocation && !submittedLocation) {
      handleSearch(urlLocation, 'url_params')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Calculate date range in days for aggregation decision
  const dateRangeDays = getDateRangeDays(dateRange)

  // Aggregate to monthly if date range > 90 days
  const shouldAggregateMonthly = dateRangeDays > 90

  // Prepare chart data
  const rawChartData = currentData?.current
    ? mergeForChart(
        {
          dates: currentData.current.time,
          temps: currentData.current.t6,
        },
        layers.includes("5yr") ? fiveYearData?.data : undefined,
        layers.includes("10yr") ? tenYearData?.data : undefined
      )
    : []

  // Apply monthly aggregation for longer date ranges
  const chartData = shouldAggregateMonthly
    ? aggregateChartToMonthly(rawChartData)
    : rawChartData

  // Get the data index that represents "today" — exact match if present,
  // otherwise the most recent past index. Powers both the verdict's hero
  // temperature and the date line.
  const todayDataIndex = (() => {
    if (!currentData?.current?.time) return -1

    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`

    const exact = currentData.current.time.findIndex((d) => d === todayStr)
    if (exact >= 0) return exact

    for (let i = currentData.current.time.length - 1; i >= 0; i--) {
      const [year, month, day] = currentData.current.time[i].split("-").map(Number)
      const dataDate = new Date(year, month - 1, day)
      if (dataDate <= today) return i
    }
    return -1
  })()

  const currentTempRaw =
    todayDataIndex >= 0 && currentData?.current?.t6
      ? currentData.current.t6[todayDataIndex]
      : undefined

  // Round once and use the rounded value for BOTH the displayed hero number
  // AND the recommendation lookup, so a 49.5°F reading doesn't display as
  // "50°F" with a "Pre-Emergent Window" phase (which evaluates against the
  // unrounded 49.5).
  const currentTemp =
    currentTempRaw !== undefined ? Math.round(currentTempRaw) : undefined

  // True when the latest available data sample is for the calendar day the
  // user is viewing the page on. When false (fresh-zip miss, source lag), we
  // drop the "Updated just now" relative line because a stale sample is not
  // freshly updated and saying so misleads the reader.
  const isDataFromToday = (() => {
    if (todayDataIndex < 0 || !currentData?.current?.time) return false
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`
    return currentData.current.time[todayDataIndex] === todayStr
  })()

  // Format the date line shown above the hero temperature. Uses the actual
  // data date (which may be yesterday on a fresh-zip miss), not "today".
  const todayDateLabel = (() => {
    if (todayDataIndex < 0 || !currentData?.current?.time) return null
    const dateStr = currentData.current.time[todayDataIndex]
    const [year, month, day] = dateStr.split("-").map(Number)
    const date = new Date(year, month - 1, day)
    if (Number.isNaN(date.getTime())) return null
    const formatted = new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
    }).format(date)
    return isDataFromToday ? formatted : `Latest data: ${formatted}`
  })()

  const trend = currentData?.current?.trendFPerDay || 0

  // Get comparison to 5-year historical average. Drives the verdict's
  // "X°F cooler than average" line. Independent of layer visibility — the
  // 5-year fetch is unconditional in handleSearch above.
  //
  // Match by MM-DD instead of dayOfYear: calculateMultiYearAverage groups
  // historical data by MM-DD and converts back to dayOfYear via a non-leap
  // reference year (2023). In a leap year, getDayOfYear(today) for any date
  // after Feb 29 is +1 vs the cached row's dayOfYear, so the integer match
  // would silently miss. MM-DD aligns both years correctly.
  const todayMMDD = (() => {
    const t = new Date()
    const month = String(t.getMonth() + 1).padStart(2, "0")
    const day = String(t.getDate()).padStart(2, "0")
    return `${month}-${day}`
  })()
  const fiveYearAvgToday = fiveYearData?.data?.find(
    (d) => d.date === todayMMDD
  )?.avgTemp

  // Get recommendation
  const recommendation =
    currentTemp !== undefined
      ? getSoilTempRecommendation(currentTemp, grassType)
      : null

  const soilTempCalendar = buildSoilTempCalendar(fiveYearData, grassType)

  useEffect(() => {
    if (!currentData || !submittedLocation) return

    const resultKey = `${submittedLocation}:${currentData.lastUpdatedAt ?? ''}`
    if (viewedResultKeyRef.current === resultKey) return

    const target = document.getElementById('soil-temp-results')
    if (!target) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return
        viewedResultKeyRef.current = resultKey
        trackEvent('soil_temp_result_viewed', {
          state: getStateFromLabel(currentData.location?.label),
          lat_band: getCoordinateBand(currentData.location?.lat),
          lon_band: getCoordinateBand(currentData.location?.lon),
          grass_type: grassType,
          current_temp_f: currentTemp,
        })
        observer.disconnect()
      },
      { threshold: 0.35 }
    )

    observer.observe(target)
    return () => observer.disconnect()
  }, [currentData, submittedLocation, grassType, currentTemp])

  const handleGrassTypeChange = useCallback((nextGrassType: GrassCategory) => {
    setGrassType(nextGrassType)
    analytics.soilTempGrassSeasonChanged({
      grassType: nextGrassType,
      currentTempF: currentTemp,
      hasResults: !!currentData,
    })
  }, [currentTemp, currentData])

  const handleZipValidationChange = useCallback((result: ZipValidationResult) => {
    setZipValidation(result)
    if (
      result.status === 'resolved' &&
      pendingGeolocationSearchRef.current === result.zip
    ) {
      pendingGeolocationSearchRef.current = null
      void handleSearch(result.zip, 'form_submit')
    }
  }, [handleSearch])

  // Resolved location shown in personalized result copy (share text, email
  // capture). Only a successful result or the shared ZIP resolver can promote
  // a five-digit string into downstream links and capture metadata.
  const submittedZip =
    currentData && submittedLocation && /^\d{5}$/.test(submittedLocation.trim())
      ? submittedLocation.trim()
      : undefined
  const resolvedInputZip =
    zipValidation.status === 'resolved' && zipValidation.zip === location.trim()
      ? zipValidation.zip
      : undefined
  const safeLawnPrefillZip = /^\d{5}$/.test(lawnPrefill.zipCode ?? '')
    ? lawnPrefill.zipCode
    : undefined
  const resultZip = submittedZip
  const resultPlace = currentData?.location?.label ?? resultZip
  const relatedLocation = currentData
    ? submittedLocation ?? undefined
    : location.trim()
      ? resolvedInputZip
      : safeLawnPrefillZip

  // Specific email-capture subcopy — names the place and the concrete windows
  // (growth-design "specificity over abstraction") instead of a generic
  // "seasonal timing notes" line.
  const emailCaptureSubcopy = resultPlace
    ? `We'll email you the moment ${resultPlace} soil temps hit the next pre-emergent, overseeding, and feeding windows, so you never miss the timing.`
    : "We'll email you the moment your soil temps hit the next pre-emergent, overseeding, and feeding windows, so you never miss the timing."

  // Share text for the verdict hero — names the temp, place, and phase.
  const shareText = currentTemp !== undefined
    ? `Soil temp${resultPlace ? ` in ${resultPlace}` : ""} is ${currentTemp}°F right now${recommendation ? ` (${recommendation.title})` : ""}. Check yours:`
    : "Check your local soil temperature by ZIP code:"

  /* National heatmap. Always on the page so the SEO promise (title says
     "Map") holds for every visitor: rendered directly below the chart once
     a lookup has run, and in the pre-lookup disclosure group otherwise.
     When ZIP results are visible, a marker pins the user's location on the
     heat overlay and a percentile callout answers "is this normal?" */
  const nationalMapSection = (
    <CollapsibleSection
      title="US Soil Temperature Map"
      defaultExpanded={true}
      onToggle={(expanded) =>
        analytics.soilTempSectionToggled({
          section: 'national_map',
          expanded,
          hasResults: !!currentData,
        })
      }
    >
      <p className="mb-3 text-xs sm:text-sm text-gray-500">
        Current 2-inch soil temperatures across the contiguous US. Refreshes every 6 hours.
      </p>
      <NationalSoilMap
        userPin={
          currentData && currentTemp !== undefined
            ? {
                lat: currentData.location.lat,
                lon: currentData.location.lon,
                label: currentData.location.label,
                t6: currentTemp,
              }
            : undefined
        }
      />
    </CollapsibleSection>
  )

  return (
    <div className="min-h-screen bg-linear-to-b from-gray-50 to-white">
      <Reveal onMount y={6} className="max-w-6xl mx-auto px-4 md:px-6 pt-4 md:pt-8 pb-8 md:pb-16">
        <BreadcrumbComponent
          items={[
            { label: "Home", href: "/" },
            { label: "Tools", href: "/tools" },
            { label: "Soil Temperature" },
          ]}
        />

        {/* Live-data heading. Becomes location-specific once results load.
            HeadlineHero carries its own bottom margin, so the wrapper only
            adds the top offset from the breadcrumb. */}
        <div className="mt-2">
          <HeadlineHero
            eyebrow="Live + 5- and 10-year averages"
            title={currentData?.location?.label ? "Soil temperature in" : "Live soil temperature"}
            accent={currentData?.location?.label ?? "by ZIP code"}
            subtitle={
              !currentData?.location
                ? "Find current soil temp in your area, compare to 5- and 10-year averages, and time pre-emergent or overseeding by depth."
                : undefined
            }
          />
        </div>

        {/* Search Section */}
        <LawnPrefillBanner lawns={lawns} selectedLawnId={selectedLawnId} onSelect={setSelectedLawnId} />
        <Card id="by-zip" className="mb-6 scroll-mt-20">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row md:items-end gap-4">
              <div className="flex-1">
                <label
                  htmlFor="soil-temp-zip"
                  className="inline-flex items-center gap-1.5 mb-2.5 text-xs font-bold uppercase tracking-wider text-green-700"
                >
                  <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                  Where
                </label>
                <ZipCodeInput
                  id="soil-temp-zip"
                  value={location}
                  onChange={(zip, meta) => {
                    userEditedPrefillRef.current = true
                    pendingGeolocationSearchRef.current =
                      meta?.entryMethod === 'geolocation' ? zip : null
                    setLocation(zip)
                    setError(null)
                  }}
                  onValidationChange={handleZipValidationChange}
                  showLabel={false}
                  rememberLastZip
                />
              </div>
              <Button
                onClick={() => handleSearch(location, 'form_submit')}
                disabled={
                  loadingStage !== 'idle' ||
                  zipValidation.status !== 'resolved' ||
                  zipValidation.zip !== location
                }
                size="lg"
                className="min-w-[120px] font-semibold bg-green-600 hover:bg-green-700 self-stretch md:self-end"
              >
                {loadingStage !== 'idle' ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {loadingStage === 'geocoding' && 'Finding...'}
                    {loadingStage === 'fetching' && 'Loading...'}
                    {loadingStage === 'historical' && 'Finishing...'}
                  </>
                ) : (
                  <>
                    <Thermometer className="mr-2 h-4 w-4" />
                    Search
                  </>
                )}
              </Button>
            </div>


            {error && (
              <div className="mt-4 p-4 rounded-lg border border-amber-200 bg-amber-50">
                <p className="text-sm text-amber-800 font-medium">{error}</p>
                <p className="text-xs text-amber-700 mt-1">
                  Try a nearby city name (e.g., "Dallas, TX"), a different ZIP code, or use the location button to auto-detect.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Methodology + answer block moved below the search card so the H1 and
            ZIP input are the first things visible. AI Overview / SERP citation
            still works because crawlers parse the full document. */}
        {!currentData && (
          <>
            <aside
              id="answer"
              aria-label="Soil temperature quick answer"
              className="mb-4 rounded-md border border-green-200 bg-green-50 px-3 py-2"
            >
              <h2 className="text-xs font-semibold text-gray-900 mb-0.5">
                What is the current soil temperature in my area?
              </h2>
              <p className="text-gray-700 text-xs leading-snug mb-1.5">
                US soil temps at 2&quot; depth run ~35&deg;F (winter) to 75&deg;F+ (late summer). Cool-season seed germinates ~50&deg;F, warm-season ~65&deg;F; crabgrass pre-emergent at ~55&deg;F. Enter your ZIP above for a live reading.
              </p>
              <div className="flex flex-wrap gap-1.5">
                <a
                  href="#germination-chart"
                  className="inline-flex items-center gap-1 rounded-full border border-green-300 bg-white px-2.5 py-1 text-xs text-green-800 hover:bg-green-100 transition"
                >
                  <Calendar className="h-3 w-3" />
                  Germination chart
                </a>
                <a
                  href="#depth-chart"
                  className="inline-flex items-center gap-1 rounded-full border border-green-300 bg-white px-2.5 py-1 text-xs text-green-800 hover:bg-green-100 transition"
                >
                  <Thermometer className="h-3 w-3" />
                  Depth chart
                </a>
              </div>
            </aside>
          </>
        )}

        {/* Results Section. The mobile reading order is verdict hero → chart →
            controls; desktop keeps controls above the chart. We achieve both
            with flex `order-*` on a single column container so the rest of the
            page below stays in normal flow. */}
        {(loadingStage !== 'idle' || currentData) && (
          <div id="soil-temp-results" className="flex min-w-0 flex-col">
            {/* VERDICT HERO — answer above proof. Renders even while
                historical data is still loading; the "X°F cooler" line is
                gated on fiveYearAvgToday so it appears once the always-on
                fetch resolves. */}
            {recommendation && currentTemp !== undefined && (
              <Card
                className={cn(
                  "order-1 mb-3 sm:mb-4",
                  STATUS_STYLES[recommendation.status].border
                )}
              >
                <CardContent className="pt-5 sm:pt-6 pb-5 sm:pb-6">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm text-gray-500">
                      {currentData?.location?.label && (
                        <span className="font-medium text-gray-700">
                          {currentData.location.label}
                        </span>
                      )}
                      {currentData?.location?.label && todayDateLabel && (
                        <span aria-hidden>·</span>
                      )}
                      {todayDateLabel && <span>{todayDateLabel}</span>}
                    </div>
                    <UnifiedShareButton
                      url={`https://whatgrassisthis.com/tools/soil-temperature?location=${encodeURIComponent((submittedLocation ?? location).trim())}`}
                      title="Soil temperature by ZIP code"
                      trackingId="soil-temperature"
                      text={shareText}
                      variant="outline"
                      size="sm"
                      className="shrink-0 min-h-[36px]"
                    />
                  </div>

                  <div
                    className={cn(
                      "text-5xl sm:text-7xl font-bold leading-none",
                      STATUS_STYLES[recommendation.status].text
                    )}
                  >
                    {currentTemp}°F
                  </div>
                  {currentData?.lastUpdatedAt && isDataFromToday && (
                    <p className="mt-0.5 text-xs text-gray-500">
                      Updated {formatRelativeAge(currentData.lastUpdatedAt)}
                    </p>
                  )}

                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-600">
                    <span>Soil temp in top 3&quot;</span>
                    <span aria-hidden>·</span>
                    {trend > 0.5 ? (
                      <span className="flex items-center gap-1 text-green-700">
                        <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4" />
                        Warming {trend.toFixed(1)}°/day
                      </span>
                    ) : trend < -0.5 ? (
                      <span className="flex items-center gap-1 text-gray-600">
                        <TrendingDown className="h-3 w-3 sm:h-4 sm:w-4" />
                        Cooling {Math.abs(trend).toFixed(1)}°/day
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-gray-500">
                        <Minus className="h-3 w-3 sm:h-4 sm:w-4" />
                        Stable
                      </span>
                    )}
                  </div>

                  {fiveYearAvgToday !== undefined && (
                    <p className="mt-1 text-xs sm:text-sm text-gray-600">
                      {getTempDifference(currentTemp, fiveYearAvgToday).label}
                    </p>
                  )}

                  {/* Phase block — the STATUS_STYLES phase tokens with the
                      phase-tool CTA promoted to a primary green Button. */}
                  <div
                    className={cn(
                      "mt-4 rounded-lg p-3 sm:p-4 border",
                      STATUS_STYLES[recommendation.status].bg,
                      STATUS_STYLES[recommendation.status].border
                    )}
                  >
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1">
                      <h2 className="font-semibold text-gray-900 text-sm sm:text-base">
                        {recommendation.title}
                      </h2>
                      <Badge
                        className={cn(
                          "text-xs",
                          STATUS_STYLES[recommendation.status].badge
                        )}
                      >
                        {recommendation.tempRange}
                      </Badge>
                      {/* Grass-season toggle. Defaults from ZIP latitude (see
                          handleSearch above); placed inline because it only
                          affects this verdict, not the page's primary temp
                          lookup. */}
                      <div className="ml-auto flex items-center gap-1.5">
                      <span className="text-xs text-gray-500">
                        Grass type
                      </span>
                      <div
                        className="inline-flex items-center rounded-full border border-gray-200 bg-white p-0.5 text-xs"
                        role="group"
                        aria-label="Grass type"
                      >
                        <button
                          type="button"
                          onClick={() => handleGrassTypeChange("cool")}
                          aria-pressed={grassType === "cool"}
                          className={cn(
                            "inline-flex min-h-[36px] items-center rounded-full px-3 text-xs transition",
                            grassType === "cool"
                              ? "bg-green-600 text-white"
                              : "text-gray-600 hover:text-gray-900"
                          )}
                        >
                          Cool
                        </button>
                        <button
                          type="button"
                          onClick={() => handleGrassTypeChange("warm")}
                          aria-pressed={grassType === "warm"}
                          className={cn(
                            "inline-flex min-h-[36px] items-center rounded-full px-3 text-xs transition",
                            grassType === "warm"
                              ? "bg-green-600 text-white"
                              : "text-gray-600 hover:text-gray-900"
                          )}
                        >
                          Warm
                        </button>
                      </div>
                      </div>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-700">
                      {recommendation.description}
                    </p>
                    {(() => {
                      const tool = getToolForPhase(recommendation.title)
                      if (!tool) return null
                      return (
                        <Button
                          asChild
                          size="sm"
                          className="mt-3 bg-green-600 hover:bg-green-700"
                        >
                          <Link
                            href={tool.href}
                            onClick={() =>
                              trackEvent("soil_temp_phase_tool_click", {
                                phase: recommendation.title,
                                target: tool.href,
                              })
                            }
                          >
                            {tool.label}
                          </Link>
                        </Button>
                      )
                    })()}
                  </div>
                </CardContent>
              </Card>
            )}

            {recommendation && currentTemp !== undefined && (
              <div className="order-5 mb-3 sm:mb-4">
                <ResultEmailCaptureCard
                  source="tool_result_soil_temperature"
                  toolDisplayName="soil-temperature plan"
                  subcopy={emailCaptureSubcopy}
                  grassType={grassType}
                  zipCode={resultZip}
                  successSubcopy="Your lawn tips are on the way. Your soil-temperature plan stays right here to revisit anytime."
                  accountLead={{
                    redirectTo: buildToolUrl("soil-temperature", {
                      location: submittedLocation ?? undefined,
                      zip: resultZip,
                    }),
                    headline: "Save my soil temperature plan",
                    subcopy: "Keep this soil-temperature plan in a free account and track your lawn over time.",
                    // Grass-aware (grassType + resultZip are passed), so the
                    // subscribe route sends the 12-month calendar. Name it, per
                    // the honesty rules in .claude/rules/tools-pages.md.
                    dividerLabel: "or get your 12-month calendar by email",
                    onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_soil_temperature"),
                  }}
                  onCaptured={() => {
                    closeNewsletterModal()
                  }}
                />
                {/* Buyer's-guide magnet: soil-temp watchers are timing a
                    pre-emergent/fertilizer purchase (2026-07-18 audit, Tier 3). */}
                <ProductGuideCta source="soil_temperature_result" variant="result" className="mt-3 sm:mt-4" />
              </div>
            )}

            {/* YEAR-AT-A-GLANCE — 12 bars of monthly average soil temp from
                5-year history, banded cold/prime/warm/hot. Stays at order-1
                so it sits between the verdict and chart on both layouts.
                Hidden until fiveYearData has resolved (the verdict can render
                first off currentData alone). */}
            {/* Reserve the calendar card's slot while the 5-year fetch is
                still out (CLS audit 2026-07-30): this card sits ABOVE the
                chart (order-1) and used to pop in on the second, slower fetch,
                pushing everything the user was already reading down ~200px. */}
            {!soilTempCalendar && currentData && (
              <Card className="order-1 mb-3 sm:mb-4" aria-hidden>
                <CardContent className="pt-5 pb-5">
                  <div className="mb-3 flex items-baseline justify-between gap-2">
                    <div className="h-4 w-28 rounded bg-gray-100 animate-pulse" />
                    <div className="h-3 w-40 rounded bg-gray-100 animate-pulse" />
                  </div>
                  <div className="h-[57px] rounded bg-gray-50 animate-pulse" />
                  <div className="mt-3 h-4 w-64 max-w-full rounded bg-gray-100 animate-pulse" />
                </CardContent>
              </Card>
            )}
            {soilTempCalendar && (
              <Card className="order-1 mb-3 sm:mb-4">
                <CardContent className="pt-5 pb-5">
                  <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-500">
                      Year at a glance
                    </div>
                    <div className="text-xs text-gray-500">
                      5-year monthly averages · 2&quot; depth
                    </div>
                  </div>
                  <YearCalendar
                    variant="full"
                    months={soilTempCalendar.cells}
                    thisMonth={soilTempCalendar.currentMonth}
                    ringColors={{ today: "#374151" }}
                    ariaLabel="12-month soil temperature averages"
                  />
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                    {soilTempCalendar.bands.map(b => (
                      <span key={b.band} className="inline-flex items-center gap-1.5">
                        <span
                          className="h-3 w-3 rounded-sm"
                          style={{ background: b.swatch }}
                        />
                        {b.label} <span className="text-gray-400">{b.range}</span>
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* CHART. order-2 on mobile (above controls), order-3 on desktop
                (below controls). */}
            <Card className="order-2 sm:order-3 mb-2 sm:mb-4 min-w-0 overflow-hidden">
              <CardContent className="pt-2 sm:pt-4 px-2 sm:px-6 min-w-0">
                {!currentData && loadingStage !== 'idle' ? (
                  <SoilTempChartSkeleton message={loadingMessage} />
                ) : (
                  <>
                    <SoilTempChart
                      ref={chartRef}
                      data={chartData}
                      layers={layers}
                      showDataLabels={!shouldAggregateMonthly}
                      title="Soil Temperature in Top 3 Inches"
                      onLayerToggle={handleLayerToggle}
                      isMonthlyView={shouldAggregateMonthly}
                      grassType={grassType}
                      isMobile={isMobile}
                    />

                    {/* Historical loading indicator below chart */}
                    {loadingStage === 'historical' && (
                      <div className="mt-2 flex items-center justify-center gap-2 text-sm text-gray-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>{loadingMessage}</span>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Date range + layer controls. order-3 on mobile (under chart),
                order-2 on desktop (above chart). */}
            {currentData && (
              <div className="order-3 sm:order-2 flex flex-col items-center sm:flex-row sm:justify-between gap-2 mb-3 sm:mb-4">
                <DateRangePicker
                  dateRange={dateRange}
                  onChange={handleDateRangeChange}
                />
                <LayerToggle layers={layers} onChange={handleLayerChange} />
              </div>
            )}

            {/* National map directly below the chart, with the user's pin. */}
            <div className="order-4 mb-3 sm:mb-4">{nationalMapSection}</div>

            {/* Rest of the result content only shows when current data ready.
                These siblings render in source order (after chart + controls)
                inside the flex column, which is the desired stacking. */}
            {currentData && (
              <>
                {/* Soil thermometer pick — page-native affiliate. Renders
                    nothing until a thermometer is added to the Directus
                    products collection (category "Lawn Mowers & Maintenance
                    Tools" with "thermometer" / "soil probe" keywords). */}
                {/* Thermometer demoted to secondary (order-7): the user is
                    already using the free digital reading, so a "buy a
                    thermometer" pitch is an intent mismatch. The phase-matched
                    products above (order-6) are the real buying moment. */}
                <div className="order-7 mt-4">
                  <ProductRecommendations
                    categories={["Lawn Mowers & Maintenance Tools"]}
                    keywords={["soil thermometer", "thermometer", "soil probe"]}
                    limit={1}
                    title="Want your own reading?"
                    trackingContext="soil-temperature-thermometer"
                    compact
                    strict
                    hideAffiliateDisclosure
                    excludeAsins={phaseProductAsins}
                  />
                </div>

                {/* Phase-specific product recommendations — pulled from the
                    Directus products collection so the high-converters
                    (e.g. Andersons PGF for fertilizer phases) surface first. */}
                {recommendation && (() => {
                  const hints = getProductHintsForPhase(recommendation.title)
                  return (
                    <div className="order-6 mt-4">
                      <ProductRecommendations
                        categories={hints.categories}
                        keywords={hints.keywords}
                        title="What to apply at this soil temp"
                        description="Matched to your current soil-temperature window: the pre-emergent, fertilizer, or seed this phase calls for."
                        trackingContext="soil-temperature-phase"
                        compact
                        onProductsShown={(products) =>
                          setPhaseProductAsins(
                            products
                              .map((p) => extractAsinFromUrl(p.affiliate_url))
                              .filter((a): a is string => Boolean(a))
                          )
                        }
                      />
                    </div>
                  )
                })()}
              </>
            )}
          </div>
        )}

        {/* Empty State - compact on mobile */}
        {!currentData && loadingStage === 'idle' && (
          <Card className="p-4 sm:p-8">
            <div className="text-center mb-4 sm:mb-8">
              <Thermometer className="h-10 w-10 sm:h-16 sm:w-16 mx-auto text-green-500 mb-2 sm:mb-4" />
              <h2 className="text-lg sm:text-xl font-semibold text-gray-700 mb-1 sm:mb-2">
                Track Soil Temperature for Better Lawn Care Timing
              </h2>
              <p className="text-gray-500 text-sm sm:text-base max-w-md mx-auto">
                Know when to overseed, apply pre-emergent, or fertilize based on actual soil conditions.
              </p>
            </div>

            {/* Quick Start Locations - more compact on mobile.
                Rendered as 3+2 centered rows on mobile (avoids the lonely
                Phoenix chip at the bottom) and a single flex-wrapped row
                on sm+ via `sm:contents` on the row wrappers. */}
            <div className="border-t pt-4 sm:pt-6">
              <p className="text-xs sm:text-sm text-gray-500 text-center mb-2 sm:mb-3">
                Try a popular location:
              </p>
              <div className="flex flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:justify-center sm:gap-2">
                {[
                  [
                    { label: "Atlanta, GA", query: "Atlanta, GA" },
                    { label: "Chicago, IL", query: "Chicago, IL" },
                    { label: "Dallas, TX", query: "Dallas, TX" },
                  ],
                  [
                    { label: "Denver, CO", query: "Denver, CO" },
                    { label: "Phoenix, AZ", query: "Phoenix, AZ" },
                  ],
                ].map((row, rowIdx) => (
                  <div
                    key={rowIdx}
                    className="flex justify-center gap-1.5 sm:contents"
                  >
                    {row.map((loc) => (
                      <Button
                        key={loc.query}
                        variant="outline"
                        size="sm"
                        className="text-xs sm:text-sm h-11 sm:h-9 px-2 sm:px-3"
                        onClick={() => handleSearch(loc.query, 'quick_start')}
                      >
                        <MapPin className="h-3 w-3 mr-1 sm:mr-1.5" />
                        {loc.label}
                      </Button>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Key Benefits - HIDDEN on mobile, shown on tablet+ for SEO */}
            <div className="hidden sm:grid md:grid-cols-3 gap-4 mt-8 pt-6 border-t">
              <div className="text-center p-4">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-2">
                  <Calendar className="h-5 w-5 text-green-600" />
                </div>
                <h3 className="font-semibold text-base text-gray-800 mb-1">Perfect Timing</h3>
                <p className="text-sm text-gray-500">
                  Know the exact window for seeding, fertilizing, and weed prevention
                </p>
              </div>
              <div className="text-center p-4">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-2">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                </div>
                <h3 className="font-semibold text-base text-gray-800 mb-1">Historical Comparison</h3>
                <p className="text-sm text-gray-500">
                  Compare current temps to 5-year and 10-year averages
                </p>
              </div>
              <div className="text-center p-4">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-2">
                  <Thermometer className="h-5 w-5 text-green-600" />
                </div>
                <h3 className="font-semibold text-base text-gray-800 mb-1">Local Data</h3>
                <p className="text-sm text-gray-500">
                  Get soil temperature specific to your ZIP code or city
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* National map + "learn the science" tables share one disclosure
            group so the inter-section gap is consistent. The map itself moves
            up beside the chart once a lookup starts (see the results column). */}
        <div className="mt-6 sm:mt-8 space-y-3">
          {loadingStage === 'idle' && !currentData && nationalMapSection}
          {/* The depth + germination reference tables moved to the server shell
              (page.tsx) so they render in crawlable static HTML instead of inside this
              useSearchParams Suspense island. The #depth-chart / #germination-chart jump
              links above still resolve to them. */}
          <div id="regional-soil-temps" className="scroll-mt-20">
            <CollapsibleSection
              title="US soil temperature by region (monthly averages)"
              defaultExpanded={false}
              onToggle={(expanded) =>
                analytics.soilTempSectionToggled({
                  section: 'regional_soil_temps',
                  expanded,
                  hasResults: !!currentData,
                })
              }
            >
              <RegionalSoilTempTable />
            </CollapsibleSection>
          </div>
        </div>

        <RelatedTools
          currentToolId="soil-temperature"
          prefillValues={{
            zip: submittedZip ?? (location.trim() ? resolvedInputZip : safeLawnPrefillZip),
            location: relatedLocation,
            grassType: lawnPrefill.grassType,
            lawnSize: lawnPrefill.lawnSizeSqft,
          }}
        />

        {/* Data sources & methodology moved to the server shell (page.tsx) —
            static prose belongs in crawlable HTML, not this bailed-out island. */}

      </Reveal>

      {/* Newsletter signup modal - shows after 1st calculator use with 3.5s delay */}
      <NewsletterSignupModal
        isOpen={showNewsletterModal}
        onClose={closeNewsletterModal}
        toolName="Soil Temperature Tracker"
        usageCount={usageCount}
        grassType={grassType}
        zipCode={submittedZip ?? (location.trim() ? resolvedInputZip : safeLawnPrefillZip)}
      />
    </div>
  )
}
